// Engagement closure (store tracker 6.55) — the quiet fills-retry affordance.
//
// A response can carry a produced answer alongside a retryable extraction
// error: the answer above (rendered as an ordinary message, never this
// component) stands on its own, but its own fill attempt failed in a way the
// server guarantees wrote nothing. This is deliberately quieter than
// `TurnError`'s full-failure card — there is no card, no swapped-out message,
// just a small notice with its own retry — because the platform amendment
// this fixes (2026-08-03 / bug cluster D, 2026-08-24 live round) is exactly
// that an answer must never be discarded to make room for a retry surface.
//
// Retrying re-runs the identical text through the normal retry path
// (`retryTurn`), which removes this message and re-sends — safe because
// `retryable !== false` is the server's own guarantee that nothing was
// written the first time.

import { RETRYABLE_FILL_NOTICE } from './constants';
import styles from './ConversationalForms.module.css';
import { useConversationalForms } from './FormsContext';
import { AlertIcon } from './icons';

type FillRetryNoticeProps = {
    /** Index of this message in the conversation — what retry removes. */
    index: number;
    /** The exact user text to re-run. */
    retryText: string;
};

const FillRetryNotice = ({ index, retryText }: FillRetryNoticeProps) => {
    const { retryTurn, state } = useConversationalForms();

    return (
        <p className={styles.fillNotice} data-testid="cf-fill-retry-notice">
            <AlertIcon size={12} />
            <span>{RETRYABLE_FILL_NOTICE}</span>
            <button
                type="button"
                className={styles.retry}
                onClick={() => retryTurn(index, retryText)}
                // A retry queued behind an in-flight turn would be confusing;
                // the composer stays open either way, so nothing is blocked.
                disabled={state.thinking}
            >
                Try again
            </button>
        </p>
    );
};

export default FillRetryNotice;
